import {
  type CreateParticipantQrBehaviourMethodsRequest,
  type CreateParticipantQrBehaviourRequest,
  type CreateParticipantQrRequest,
  type ParticipantBehaviour,
  ParticipantQrStatus,
} from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import { z } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { HookedSubmitButton } from '../../components/forms/V3HookedSubmitButton';
import { createModal } from '../../components/generic/Modal';
import { useCreateParticipantQrMutation } from '../../graphql/hooks/useCreateParticipantQrMutation';
import {
  useParticipantBehavioursQuery,
  useParticipantEmbeddableFormsQuery,
} from '../../graphql/hooks/useParticipantQuery';
import { useAppContext } from '../../hooks/useAppContext';
import { zodResolver } from '../../utils/zod';
import { ParticipantQRForm } from './ParticipantQRForm';

type FormValues = CreateParticipantQrRequest;

const createParticipantQrBehaviourMethodsRequestSchema: z.ZodSchema<CreateParticipantQrBehaviourMethodsRequest> =
  z.object({
    abc: z.object({ enabled: z.boolean() }),
    frequency: z.object({ enabled: z.boolean() }),
    duration: z.object({ enabled: z.boolean() }),
    episodicSeverity: z.object({ enabled: z.boolean() }),
  });

export const behaviourSchema: z.ZodSchema<CreateParticipantQrBehaviourRequest> = z.object({
  behaviourId: z.string(),
  methods: createParticipantQrBehaviourMethodsRequestSchema,
});

export const participantQrRequestSchema: z.ZodSchema<FormValues> = z.object({
  name: z.string().nonempty('Name is required'),
  status: z.enum(ParticipantQrStatus),
  behaviours: z.array(behaviourSchema),
  embeddableFormIds: z.array(z.string()),
  labelIds: z.array(z.string()),
});

interface Props {
  participantId: string;
}

export const ParticipantQRCreateModal = createModal<Props>((props) => {
  const { participantId, modal } = props;
  const { visible, hide, remove } = modal;

  const { organization, successToast, errorToast } = useAppContext();

  const behavioursQuery = useParticipantBehavioursQuery({
    organizationId: organization.id,
    participantId,
  });

  const embeddableFormsQuery = useParticipantEmbeddableFormsQuery({
    organizationId: organization.id,
    participantId,
  });

  const participantBehaviours =
    (behavioursQuery.data?.organization?.participant?.behaviours as ParticipantBehaviour[]) ?? [];

  const embeddableForms =
    embeddableFormsQuery.data?.organization?.participant?.embeddableForms ?? [];

  const initialValues: FormValues = {
    name: '',
    status: ParticipantQrStatus.Active,
    behaviours: participantBehaviours.map((behaviour) => ({
      behaviourId: behaviour.id,
      methods: {
        abc: { enabled: false },
        frequency: { enabled: false },
        duration: { enabled: false },
        episodicSeverity: { enabled: false },
      },
    })),
    embeddableFormIds: [],
    labelIds: [],
  };

  const methods = useForm<FormValues>({
    values: initialValues,
    mode: 'all',
    resolver: zodResolver(participantQrRequestSchema),
  });

  const mutation = useCreateParticipantQrMutation();

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      const behavioursToSubmit = values.behaviours.filter((behaviour) =>
        Object.values(behaviour.methods).some((method) => method.enabled),
      );

      await mutation
        .mutateAsync({
          organizationId: organization.id,
          participantId,
          request: {
            name: values.name,
            status: values.status,
            behaviours: behavioursToSubmit,
            embeddableFormIds: values.embeddableFormIds,
            labelIds: values.labelIds,
          },
        })
        .then(() => {
          hide();
          successToast('QR code created');
        })
        .catch((err) => {
          errorToast(`Error creating QR code: ${err.message}`);
        });
    },
    [errorToast, hide, mutation, organization.id, participantId, successToast],
  );

  return (
    <ChakraV3.Dialog.Root open={visible} onOpenChange={hide} onExitComplete={remove}>
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ChakraV3.Dialog.Content>
              <ChakraV3.DialogTitleSubtitle
                title='Add QR code'
                subtitle='You can create a new QR code for this participant'
              />
              <ChakraV3.Dialog.Body>
                <ParticipantQRForm
                  participantBehaviours={participantBehaviours}
                  participantEmbeddableForms={embeddableForms}
                />
              </ChakraV3.Dialog.Body>
              <ChakraV3.Dialog.Footer>
                <ChakraV3.HStack>
                  <ChakraV3.Button onClick={hide} size='sm' variant='subtle'>
                    Close
                  </ChakraV3.Button>
                  <HookedSubmitButton>Create QR</HookedSubmitButton>
                </ChakraV3.HStack>
              </ChakraV3.Dialog.Footer>
            </ChakraV3.Dialog.Content>
          </form>
        </FormProvider>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
